import InputAdornment from '@mui/material/InputAdornment';
import { FormEvent, useEffect, useId, useRef, useState } from 'react';
import { Eye, EyeOff } from 'react-feather';

import { isValidPassword } from '@/auth';
import { ResendCode } from '@/auth/components/shared';
import Button from '@/componentsv2/Button';
import IconButton from '@/componentsv2/IconButton';
import Input from '@/componentsv2/Input';

import { toFieldErrors, useConfirmReset } from './useConfirmReset';

/**
 * The first is shown above the request form on the Cognito path (ENG-6323);
 * the second when an emailed reset link from before the switch is opened (ENG-6104).
 */
export const RESET_REQUEST_COPY =
  "We'll email you a code. Enter it below and choose a new password.";
export const LEGACY_LINK_COPY = 'That reset link no longer works. Request a new code below.';

const PASSWORD_HINT =
  'At least 8 characters, with an uppercase letter, a lowercase letter, a digit and a symbol.';

export interface ResetActions {
  /** Emails a code (first or resend); resolves the same whether or not the address has an account. */
  requestCode: (email: string) => Promise<void>;
  confirmReset: (email: string, code: string, newPassword: string) => Promise<void>;
  /** Signs in with the new password; a rejection leaves the user on a "go to login" screen. */
  signIn: (email: string, password: string) => Promise<void>;
}

interface CognitoPasswordResetProps {
  actions: ResetActions;
  /** The page was opened from a legacy `password_token` link. */
  legacyLink?: boolean;
  /** Where "Go to Login" points when the sign-in after the reset stops; carries `next`. */
  loginHref?: string;
  /** A code was already sent to this address: start at the confirm step. */
  sentTo?: string;
}

type Step = { name: 'request' } | { name: 'confirm'; email: string } | { name: 'reset' };

interface FormErrorProps {
  message?: string;
}

function FormError({ message }: FormErrorProps) {
  if (!message) return null;
  return (
    <p role="alert" className="text-[var(--errorText)]">
      {message}
    </p>
  );
}

interface RequestFormProps {
  requestCode: ResetActions['requestCode'];
  legacyLink?: boolean;
  onSent: (email: string) => void;
}

function RequestForm({ requestCode, legacyLink, onSent }: RequestFormProps) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await requestCode(email);
      onSent(email.trim());
    } catch (err) {
      setError(toFieldErrors(err).form);
      setBusy(false);
    }
  }

  return (
    <form className="flex flex-col gap-16" onSubmit={handleSubmit}>
      {legacyLink && <p className="text-on-surface">{LEGACY_LINK_COPY}</p>}
      <p className="text-on-surface">{RESET_REQUEST_COPY}</p>
      <Input
        label="Email address"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        inputProps={{ autoComplete: 'email' }}
      />
      <FormError message={error} />
      <Button type="submit" className="min-h-48" disabled={!email.trim() || busy}>
        Send code
      </Button>
    </form>
  );
}

type ConfirmFormProps = Parameters<typeof useConfirmReset>[0];

function ConfirmForm(props: ConfirmFormProps) {
  const {
    code,
    setCode,
    password,
    setPassword,
    errors,
    busy,
    resent,
    secondsLeft,
    handleSubmit,
    handleResend,
  } = useConfirmReset(props);
  const codeId = useId();
  const instructionsId = useId();
  const codeRef = useRef<HTMLInputElement>(null);
  const [showPassword, setShowPassword] = useState(false);

  // The request form held focus and is gone: start keyboard and screen-reader users on the code.
  useEffect(() => {
    codeRef.current?.focus();
  }, []);

  // MUI names the helper `${id}-helper-text`; inputProps overrides its describedby, so keep both.
  const codeDescribedBy = [instructionsId, errors.code && `${codeId}-helper-text`]
    .filter(Boolean)
    .join(' ');

  return (
    <form className="flex flex-col gap-16" onSubmit={handleSubmit}>
      <p id={instructionsId} className="text-on-surface">
        If an account exists for {props.email}, we sent it a 6-digit code. Enter it below with your
        new password.
      </p>
      <Input
        id={codeId}
        label="Code"
        inputRef={codeRef}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        error={!!errors.code}
        helperText={errors.code}
        inputProps={{
          autoComplete: 'one-time-code',
          inputMode: 'numeric',
          'aria-describedby': codeDescribedBy,
        }}
      />
      <Input
        label="New password"
        type={showPassword ? 'text' : 'password'}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={!!errors.password}
        helperText={errors.password ?? PASSWORD_HINT}
        inputProps={{ autoComplete: 'new-password' }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onClick={() => setShowPassword((shown) => !shown)}
              >
                {showPassword ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <FormError message={errors.form} />
      {/* Stays mounted (and in the accessibility tree, so not hidden) so screen readers
          announce the text when a resend lands; while empty, the negative margin cancels its gap. */}
      <p role="status" aria-live="polite" className="text-on-surface empty:-mt-16">
        {resent ? 'We sent a new code.' : ''}
      </p>
      <Button
        type="submit"
        className="min-h-48"
        disabled={!code.trim() || !isValidPassword(password) || busy}
      >
        Set password
      </Button>
      <ResendCode
        className="self-center"
        secondsLeft={secondsLeft}
        onResend={() => void handleResend()}
        disabled={busy}
      />
    </form>
  );
}

interface ResetDoneProps {
  loginHref: string;
}

function ResetDone({ loginHref }: ResetDoneProps) {
  const messageRef = useRef<HTMLParagraphElement>(null);

  // The confirm form held focus and is gone: start keyboard and screen-reader users on the result.
  useEffect(() => {
    messageRef.current?.focus();
  }, []);

  return (
    <>
      <p ref={messageRef} tabIndex={-1} role="status" className="text-on-surface outline-none">
        Your password has been reset. You can now log in.
      </p>
      {/* A document load: the sign-in that stopped cleared any old session, but the viewer
          in the store survives a router push and Login would redirect it straight on to next. */}
      <Button to={loginHref} reloadDocument className="min-h-48">
        Go to Login
      </Button>
    </>
  );
}

/**
 * Cognito forgot-password (ENG-6104): request a code by email, then set a new
 * password with it and sign in. Never says whether an address has an account.
 */
export default function CognitoPasswordReset({
  actions,
  legacyLink,
  loginHref = '/login',
  sentTo,
}: CognitoPasswordResetProps): JSX.Element {
  const [step, setStep] = useState<Step>(
    sentTo ? { name: 'confirm', email: sentTo } : { name: 'request' }
  );

  return (
    <div className="mx-auto mt-24 flex max-w-[420px] flex-col gap-16">
      <h1 className="text-24 font-bold text-on-surface">Reset your password</h1>
      {step.name === 'request' && (
        <RequestForm
          requestCode={actions.requestCode}
          legacyLink={legacyLink}
          onSent={(email) => setStep({ name: 'confirm', email })}
        />
      )}
      {step.name === 'confirm' && (
        <ConfirmForm
          email={step.email}
          confirmReset={actions.confirmReset}
          signIn={actions.signIn}
          requestCode={actions.requestCode}
          onSignInStopped={() => setStep({ name: 'reset' })}
        />
      )}
      {step.name === 'reset' && <ResetDone loginHref={loginHref} />}
    </div>
  );
}
